import React, { useState, useEffect } from 'react';
import '@/App.css';
import '@/i18n';
import { useTranslation } from 'react-i18next';
import { useAstronomicalData } from '@/hooks/useAstronomicalData';
import { getCelestialBackground, getTextColors, getSurfaceColors } from '@/utils/celestialBackground';
import DynamicBackground from '@/components/DynamicBackground';
import UnifiedHeroCard from '@/components/UnifiedHeroCard';
import AstronomyPanel from '@/components/AstronomyPanel';
import DualCalendarWidget from '@/components/DualCalendarWidget';
import NewMoonCountdown from '@/components/NewMoonCountdown';
import LanguageToggle from '@/components/LanguageToggle';
import ShareButton from '@/components/ShareButton';
import CitySelector from '@/components/CitySelector';
import PrayerTimesWidget from '@/components/PrayerTimesWidget';
import QiblaCompass from '@/components/NewQiblaCard';
import ClockTypeSelector from '@/components/ClockTypeSelector';
import FeedbackButton from '@/components/FeedbackButton';
import TemperatureBadge from '@/components/TemperatureBadge';
import PrivacyPage from '@/components/PrivacyPage';
import TermsPage from '@/components/TermsPage';
import PrivacyConsentModal from '@/components/PrivacyConsentModal';
import WelcomeLanguageModal from '@/components/WelcomeLanguageModal';
import AboutPage from '@/components/AboutPage';
import HamburgerMenu from '@/components/HamburgerMenu';
import LanguageModal from '@/components/LanguageModal';
import SmartReviewModal from '@/components/SmartReviewModal';
import AdminPage from '@/components/admin/AdminPage';
import { Menu, Globe } from 'lucide-react';
import HuaweiRedirect from '@/components/HuaweiRedirect';
import GooglePlayRedirect from '@/components/GooglePlayRedirect';
import IosComingSoon from '@/components/IosComingSoon';
import TamamPreview from '@/components/TamamPreview';
import AudioNotificationsPreview from '@/components/AudioNotificationsPreview';
import LanguagesShowcase from '@/components/LanguagesShowcase';
import { POPULAR_CITIES, findCityById } from '@/utils/cities';

function App() {
  // Simple client-side routing: /privacy renders the policy, /download now opens
  // /about, /huawei redirects to AppGallery, /google and /ios show
  // Coming Soon pages, everything else renders the main app. SPA hosts serve
  // index.html for any path so direct links work out of the box.
  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (path === '/privacy') return <PrivacyPage />;
    if (path === '/terms') return <TermsPage />;
    if (path === '/download') {
      // Former "Falak Services" page — merged into /about; keep old links working.
      window.history.replaceState(null, '', '/about');
      return <AboutPage />;
    }
    if (path === '/about') return <AboutPage />;
    if (path === '/admin') return <AdminPage />;
    if (path === '/huawei') return <HuaweiRedirect />;
    if (path === '/google') return <GooglePlayRedirect />;
    if (path === '/ios') return <IosComingSoon />;
    if (path === '/tamam-preview') return <TamamPreview />;
    if (path === '/audio-preview') return <AudioNotificationsPreview />;
    if (path === '/languages-showcase') return <LanguagesShowcase />;
  }
  return <FalakApp />;
}

function FalakApp() {
  const { t, i18n } = useTranslation();
  const [selectedCity, setSelectedCity] = useState(null);
  const [clockType, setClockType] = useState(() => localStorage.getItem('falak.clockType') || 'digital');
  const [useGPS, setUseGPS] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  // Restore selected city from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('falak.cityId');
    if (stored) {
      const city = findCityById(stored);
      if (city) {
        setSelectedCity(city);
        setUseGPS(false);
      }
    }
  }, []);

  // Effective location: GPS or selected city
  const { location: gpsLocation, gpsResolved, astronomicalData, loading } = useAstronomicalData(
    selectedCity ? { lat: selectedCity.lat, lon: selectedCity.lon, timezone: selectedCity.timezone } : null,
    useGPS
  );

  const location = selectedCity
    ? { lat: selectedCity.lat, lon: selectedCity.lon }
    : gpsLocation;

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setUseGPS(false);
    localStorage.setItem('falak.cityId', city.id);
  };

  const handleUseGPS = () => {
    setSelectedCity(null);
    setUseGPS(true);
    localStorage.removeItem('falak.cityId');
  };

  const handleClockTypeChange = (type) => {
    setClockType(type);
    localStorage.setItem('falak.clockType', type);
  };

  // Set language direction (Arabic + Urdu + Persian are RTL)
  useEffect(() => {
    const rtlLangs = ['ar', 'ur', 'fa'];
    document.documentElement.dir = rtlLangs.includes(i18n.language) ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const sunPosition = astronomicalData?.sun_position;
  const moonPhase = astronomicalData?.moon_phase;
  const hijriDate = astronomicalData?.hijri_date;

  const celestialStyle = getCelestialBackground(sunPosition);
  const textColors = getTextColors(sunPosition?.phase || 'night');
  const surfaceColors = getSurfaceColors(sunPosition?.phase || 'night');

  // Language display for the pill in the sticky header and the menu.
  const LANG_LABELS = {
    en: 'English', ar: 'العربية', ur: 'اردو', zh: '中文', fa: 'فارسی',
    tr: 'Türkçe', fr: 'Français', bn: 'বাংলা', id: 'Indonesia',
    es: 'Español', pt: 'Português', de: 'Deutsch', it: 'Italiano',
  };
  const currentLangLabel = LANG_LABELS[i18n.language] || 'English';

  return (
    <DynamicBackground celestialStyle={celestialStyle}>
      <PrivacyConsentModal />
      <WelcomeLanguageModal />
      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} />
      <SmartReviewModal forceOpen={reviewOpen} onClose={() => setReviewOpen(false)} />
      <HamburgerMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onOpenLanguage={() => setLangOpen(true)}
        onOpenReview={() => { setMenuOpen(false); setReviewOpen(true); }}
        currentLangLabel={currentLangLabel}
        citySelectorSlot={
          <CitySelector
            selectedCity={selectedCity}
            onSelect={handleCitySelect}
            onUseGPS={handleUseGPS}
            textColors={textColors}
            gpsLocation={gpsLocation}
            gpsResolved={gpsResolved}
          />
        }
        clockTypeSelectorSlot={
          <ClockTypeSelector
            clockType={clockType}
            onChange={handleClockTypeChange}
            textColors={textColors}
            surfaceColors={surfaceColors}
          />
        }
        shareButtonSlot={<ShareButton textColors={textColors} />}
        feedbackButtonSlot={<FeedbackButton textColors={textColors} inline />}
      />

      <div id="app-container" className="min-h-screen">
        {/* Sticky compact header — logo · language · temp · hamburger */}
        <header
          className="sticky top-0 z-50 px-4 py-3 md:px-6 md:py-4"
          style={{
            background: 'rgba(11, 19, 43, 0.55)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
            {/* Left — Logo + App name */}
            <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
              <div
                className="relative w-9 h-9 rounded-full flex-shrink-0"
                style={{
                  background: `radial-gradient(circle at 30% 30%, ${textColors.accent} 0%, ${textColors.accent}88 40%, transparent 70%)`,
                  boxShadow: `0 0 24px ${textColors.accent}44`,
                }}
                data-testid="falak-logo"
              >
                <div
                  className="absolute inset-1 rounded-full"
                  style={{
                    background: textColors.primary === '#FFFFFF'
                      ? 'radial-gradient(circle at 70% 50%, transparent 50%, #050914 51%)'
                      : 'radial-gradient(circle at 70% 50%, transparent 50%, #FFFFFF 51%)',
                    opacity: 0.85,
                  }}
                />
              </div>
              <h1
                className="text-heading text-xl md:text-2xl"
                style={{ color: textColors.primary, letterSpacing: i18n.language === 'ar' ? '0' : '-0.02em' }}
                data-testid="app-title"
              >
                {t('appName')}
              </h1>
            </div>

            {/* Center — Language pill (compact but readable) */}
            <button
              onClick={() => setLangOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all hover:-translate-y-0.5"
              style={{
                background: 'rgba(245, 158, 11, 0.14)',
                borderColor: 'rgba(245, 158, 11, 0.45)',
                color: textColors.primary,
                maxWidth: '38%',
              }}
              data-testid="header-language-pill"
              aria-label="Change language"
            >
              <Globe size={15} style={{ color: '#F59E0B', flexShrink: 0 }} />
              <span className="text-xs font-medium truncate">{currentLangLabel}</span>
            </button>

            {/* Right — Temperature + Hamburger */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <TemperatureBadge location={location} textColors={textColors} />
              <button
                onClick={() => setMenuOpen(true)}
                className="p-2 rounded-lg border transition-all hover:bg-white/10"
                style={{
                  borderColor: 'rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.05)',
                  color: textColors.primary,
                }}
                data-testid="header-hamburger"
                aria-label="Open menu"
              >
                <Menu size={20} />
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pb-12 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            {loading ? (
              <div
                className="flex items-center justify-center h-[50vh]"
                style={{ color: textColors.secondary }}
                data-testid="loading-state"
              >
                <div className="text-center">
                  <div className="loading-shimmer w-16 h-16 rounded-full mx-auto mb-4"></div>
                  <p className="text-body">{t('loading')}</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 fade-in">
                {/* Unified Hero - merged clock + dates + moon */}
                <div className="lg:col-span-12">
                  <UnifiedHeroCard
                    moonPhase={moonPhase}
                    hijriDate={hijriDate}
                    surfaceColors={surfaceColors}
                    textColors={textColors}
                    clockType={clockType}
                  />
                </div>

                {/* New Moon Countdown - city-specific crescent visibility */}
                <div className="lg:col-span-6 fade-in-delay-1">
                  <NewMoonCountdown
                    moonPhase={moonPhase}
                    surfaceColors={surfaceColors}
                    textColors={textColors}
                    location={location}
                    timezone={selectedCity?.timezone || astronomicalData?.timezone}
                    cityLabel={selectedCity ? (i18n.language === 'ar' ? selectedCity.name_ar : selectedCity.name_en) : null}
                  />
                </div>

                {/* Astronomy Panel - paired with New Moon */}
                <div className="lg:col-span-6 fade-in-delay-1">
                  <AstronomyPanel
                    sunPosition={sunPosition}
                    timezone={selectedCity?.timezone || astronomicalData?.timezone}
                    surfaceColors={surfaceColors}
                    textColors={textColors}
                  />
                </div>

                {/* Prayer Times - full width for prominence */}
                <div className="lg:col-span-12 fade-in-delay-2">
                  <PrayerTimesWidget
                    location={location}
                    timezone={selectedCity?.timezone || astronomicalData?.timezone}
                    selectedCity={selectedCity}
                    surfaceColors={surfaceColors}
                    textColors={textColors}
                    celestialStyle={celestialStyle}
                    sunPhase={sunPosition?.phase || 'night'}
                  />
                </div>

                {/* Qibla Compass - full width, prominent */}
                <div className="lg:col-span-12 fade-in-delay-2">
                  <QiblaCompass
                    location={location}
                    cityLabel={selectedCity ? (i18n.language === 'ar' ? selectedCity.name_ar : selectedCity.name_en) : null}
                    surfaceColors={surfaceColors}
                    textColors={textColors}
                  />
                </div>

                {/* Dual Calendar */}
                <div className="lg:col-span-12 fade-in-delay-3">
                  <DualCalendarWidget
                    surfaceColors={surfaceColors}
                    textColors={textColors}
                  />
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Footer */}
        <footer className="pb-6 px-4 text-center">
          <p
            className="text-xs md:text-sm text-body"
            style={{ color: textColors.secondary }}
            data-testid="footer-text"
          >
            {t('tagline')}
          </p>
        </footer>

        {/* Floating feedback button (always accessible) */}
        <FeedbackButton textColors={textColors} selectedCity={selectedCity} />
      </div>
    </DynamicBackground>
  );
}

export default App;
